// Workflows Teil 2 (Runde 10, Agent 10A): Typen und Client für Läufe-Berichte, Blackout Dates, Sendefenster und Ziele.
// Routen: API.md 19.1a (`api/crm_api/routers/workflows_teil2.py`). Die Grundtypen der Workflows stehen in `api/workflows.ts`.
import { http } from "../api/client";
import type { Liste } from "../api/types";
import type { SchrittArt, WorkflowLauf } from "../api/workflows";

// ---------- Ziele und Sendefenster (liegen in `workflow.einstellungen`) ----------

export type ZielArt = "status" | "mail_eingang" | "sms_eingang" | "anruf" | "custom_activity" | "bedingung";

export interface Ziel {
  id: string;
  art: ZielArt;
  name?: string | null;
  /** status: {nach} · anruf: {richtung, disposition, min_dauer_s} · custom_activity: {type_id, bedingungen} · bedingung: {bedingungen} */
  config: Record<string, unknown>;
}

export interface Sendefenster {
  aktiv: boolean;
  /** 0 = Montag … 6 = Sonntag */
  tage: number[];
  von: string;
  bis: string;
  zeitzone: "organisation" | "kontakt";
  streuung_min: number;
  auch_aufgaben: boolean;
}

/** Ein Lauf, wie ihn die API seit Runde 10 liefert: bei erreichtem Ziel steht `ziel_erreicht` da. */
export type LaufMitZiel = WorkflowLauf & { ziel_erreicht?: { ziel: { id: string; art: ZielArt; name: string } | null; zeit: string | null } | null };

// ---------- Blackout Dates ----------

export interface BlackoutTag {
  /** JJJJ-MM-TT */
  datum: string;
  name: string;
  jaehrlich: boolean;
}

export interface BlackoutAntwort {
  tage: BlackoutTag[];
  jahr: number;
  region: string;
  /** gesetzliche Feiertage der Region der Organisation im gefragten Jahr (zum Übernehmen) */
  feiertage: { datum: string; name: string; quelle: string }[];
}

export interface VorschauAntwort {
  sendefenster: Sendefenster;
  text: string;
  naechste: string[];
  zone: string;
}

// ---------- Berichte ----------

export type LaufGruppe = "aktiv" | "pausiert" | "fertig" | "ziel" | "abgebrochen" | "fehler";
export type SchrittKlasse = "erreicht" | "ok" | "uebersprungen" | "fehler" | "wartet";

export interface BerichtGesamt {
  eingeschrieben: number;
  aktiv: number;
  pausiert: number;
  fertig: number;
  ziel: number;
  abgebrochen: number;
  fehler: number;
  abschluss_quote: number | null;
  ziel_quote: number | null;
  fehler_quote: number | null;
  mittlere_dauer_s: number | null;
  mittlere_zeit_bis_ziel_s: number | null;
  mail_entwuerfe: number;
  sms_entwuerfe: number;
  aufgaben: number;
}

export interface BerichtSchritt {
  id: string;
  nummer: number;
  art: SchrittArt;
  name: string | null;
  erreicht: number;
  erreicht_quote: number | null;
  ok: number;
  uebersprungen: number;
  fehler: number;
  wartet: number;
  ziel_danach: number;
}

export interface Bericht {
  workflow_id: string;
  name: string;
  status: string;
  version: number;
  von: string;
  bis: string;
  hat_ziele: boolean;
  gesamt: BerichtGesamt;
  schritte: BerichtSchritt[];
  ziel_vor_dem_ersten_schritt: number;
  ziele: { id: string; art: ZielArt; name: string; erreicht: number }[];
  aufloesung: "tag" | "woche";
  verlauf: { tag: string; eingeschrieben: number; ziel: number; beendet: number }[];
  gekappt: boolean;
}

export interface UebersichtZeile {
  workflow_id: string;
  name: string;
  status: string;
  schritte: number;
  hat_ziele: boolean;
  eingeschrieben: number;
  aktiv: number;
  pausiert: number;
  fertig: number;
  ziel: number;
  abgebrochen: number;
  fehler: number;
  ziel_quote: number | null;
  fehler_quote: number | null;
  letzter_lauf_at: string | null;
}

export interface BerichtLaeufeFilter {
  von?: string;
  bis?: string;
  gruppe?: LaufGruppe;
  schritt_id?: string;
  klasse?: SchrittKlasse;
  ziel_nach?: string;
  limit?: number;
  cursor?: string;
}

const id = encodeURIComponent;

export const workflows2Api = {
  uebersicht: (von?: string, bis?: string) => http.get<{ von: string; bis: string; data: UebersichtZeile[] }>("/api/workflows/berichte/uebersicht", { von, bis }),
  bericht: (workflowId: string, von?: string, bis?: string) => http.get<Bericht>(`/api/workflows/${id(workflowId)}/bericht`, { von, bis }),
  berichtLaeufe: (workflowId: string, f: BerichtLaeufeFilter = {}) =>
    http.get<Liste<WorkflowLauf>>(`/api/workflows/${id(workflowId)}/bericht/laeufe`, { ...f }),
  blackout: (jahr?: number) => http.get<BlackoutAntwort>("/api/workflows/einstellungen/blackout", { jahr }),
  blackoutSpeichern: (tage: BlackoutTag[]) => http.put<{ tage: BlackoutTag[] }>("/api/workflows/einstellungen/blackout", { tage }),
  vorschau: (sendefenster: Sendefenster, blackoutBeachten: boolean, anzahl = 5) =>
    http.post<VorschauAntwort>("/api/workflows/einstellungen/sendefenster-vorschau", { sendefenster, blackout_beachten: blackoutBeachten, anzahl }),
};
